import React from 'react';
import CommonTable from '../../../Components/CommonTable';
import NoDataPage from '../../../Components/NoDataPage';
import StatusBadge from '../../../Components/StatusBadge';
import CommonDropdown from '../../../Components/CommonDropdown';
import CommonButton from '../../../Components/CommonButton';
import CommonPagination from '../../../Components/CommonPagination';
import ModuleIcon from '../../../Components/ModuleIcon';
import StatCard from '../../../Components/StatCard';
import AddOnCard from '../../../Dashboard/AddOns/AddOnCard';
import RiskCategoryDonut from '../components/RiskCategoryDonut';
import PortfolioTrendChart, { TrendBadge } from '../components/PortfolioTrendChart';
import RiskHeatmap from '../components/RiskHeatmap';
import { fontWeight, fontSize, Heading, SubHeading, Body } from '../../../Constants/Fonts';

// OverviewDashboard — the org-wide analytics home shown to every role EXCEPT
// risk_owner / risk_manager (who get focused homes). Greeting header, severity
// KPIs, exposure + category + top-risks row, trend + heatmap + treatment row,
// and the BU/Project distribution tables. Data comes via `dash`
// (useRiskDashboard); actions (open detail, navigate, scope) are passed in.

// Rows per page for the BU / Project distribution tables on the dashboard.
const DIST_PER_PAGE = 4;

// ── Severity KPI tiles — presentational config (icon + colour per severity) ──
const KPIS = [
    { key: 'total', label: 'Total Risks', icon: 'score', color: 'var(--color-primary)', soft: 'var(--color-primary-soft)' },
    { key: 'critical', label: 'Critical', icon: 'violation', color: 'var(--color-critical)', soft: 'var(--color-critical-soft)' },
    { key: 'high', label: 'High', icon: 'risk', color: 'var(--color-high)', soft: 'var(--color-high-soft)' },
    { key: 'medium', label: 'Medium', icon: 'risk', color: 'var(--color-medium)', soft: 'var(--color-medium-soft)' },
    { key: 'low', label: 'Low', icon: 'compliance', color: 'var(--color-low)', soft: 'var(--color-low-soft)' },
];

// ── Risk distribution tables (BU + Project breakdowns) ──────────────────────
const DIST_SEV = {
    critical: 'var(--color-critical)',
    high: 'var(--color-high)',
    medium: 'var(--color-medium)',
    low: 'var(--color-low)',
    open: 'var(--color-text-secondary)',
};

const distNumHead = (text) => (
    <Body as="span" variant={2} weight="medium" style={{ fontSize: fontSize.sm, display: 'block', textAlign: 'right', letterSpacing: '0.04em' }}>{text}</Body>
);

const distNum = (count, sevKey) => {
    const zero = !count;
    return (
        <Body
            as="span"
            color={zero ? 'var(--color-text-muted)' : DIST_SEV[sevKey]}
            style={{ fontWeight: zero ? fontWeight.regular : fontWeight.semibold, display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
        >
            {count || 0}
        </Body>
    );
};

const distTotal = (value) => (
    <Body as="span" color="var(--color-text-primary)" weight="bold" style={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        {value}
    </Body>
);

const distributionHeaders = (firstColLabel) => [
    firstColLabel,
    distNumHead('Total'),
    distNumHead('Critical'),
    distNumHead('High'),
    distNumHead('Medium'),
    distNumHead('Low'),
    distNumHead('Open'),
];

const distributionRows = (rows) =>
    rows.map((r) => ({
        rowStyle: r.muted ? { fontStyle: 'italic' } : undefined,
        data: [
            <Body as="span" color="var(--color-text-primary)" weight="medium">
                {r.name}
                {r.code && <Body as="span" variant={2} weight="medium" style={{ fontSize: fontSize.sm, color: 'var(--color-text-muted)', marginLeft: 6 }}>({r.code})</Body>}
            </Body>,
            distTotal(r.total),
            distNum(r.critical, 'critical'),
            distNum(r.high, 'high'),
            distNum(r.medium, 'medium'),
            distNum(r.low, 'low'),
            distNum(r.open, 'open'),
        ],
    }));

// Greeting: time-of-day + the signed-in user's first name (from local cache).
const greetUser = () => {
    let hour = 12;
    try { hour = new Date().getHours(); } catch { /* keep default */ }
    const part = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
    let name = '';
    try {
        const u = JSON.parse(window.localStorage.getItem('risk_portal_user') || '{}');
        name = u.first_name || '';
        if (!name) {
            const p = JSON.parse(window.localStorage.getItem('user_profile') || '{}');
            name = p.first_name || p.name || '';
        }
    } catch { /* ignore */ }
    return name ? `${part}, ${name}` : part;
};

// Small tinted icon chip used in card headers.
const CardIcon = ({ name }) => (
    <span className="rp1_dash_head_icon"><ModuleIcon name={name} size={18} /></span>
);

// Card header: icon chip + title (+ subtitle), optional right-side action.
const CardHead = ({ icon, title, subtitle, action }) => (
    <div className="rp1_dash_card_head">
        <div className="rp1_dash_card_headleft">
            {icon && <CardIcon name={icon} />}
            <div className="rp1_dash_card_titles">
                <SubHeading variant={2} color="var(--color-text-primary)" style={{ margin: 0 }}>{title}</SubHeading>
                {subtitle && <Body variant={2} color="var(--color-text-muted)" style={{ margin: 0 }}>{subtitle}</Body>}
            </div>
        </div>
        {action}
    </div>
);

export default function OverviewDashboard({
    dash,
    businessUnit,
    project,
    onBusinessUnit,
    onProject,
    can,
    goTo,
    onOpenRisk,
    onCreateRisk,
}) {
    const total = dash.totalRisks || 0;
    const severityValue = (key) => (key === 'total' ? total : dash.severity[key]);
    const pctOfTotal = (v) => (total > 0 ? Math.round(((v || 0) / total) * 100) : 0);

    // Client-side paging for the two distribution tables (4 rows/page).
    const [buPage, setBuPage] = React.useState(0);
    const [projectPage, setProjectPage] = React.useState(0);
    const pageSlice = (rows, page) => rows.slice(page * DIST_PER_PAGE, page * DIST_PER_PAGE + DIST_PER_PAGE);
    const pageCount = (rows) => Math.ceil(rows.length / DIST_PER_PAGE);

    // Total-risks trend hint from the 6-month portfolio series (real data).
    const trend = dash.trend || [];
    const totalDelta = (() => {
        if (trend.length < 2) return null;
        const first = trend[0].open || 0;
        const last = trend[trend.length - 1].open || 0;
        if (!first) return null;
        return Math.round(((last - first) / first) * 100);
    })();
    const kpiHint = (key) => {
        if (key !== 'total') return `${pctOfTotal(severityValue(key))}% of total`;
        if (totalDelta == null) return 'Across all business units';
        return `${totalDelta <= 0 ? '▼' : '▲'} ${Math.abs(totalDelta)}% vs 6 months ago`;
    };
    const kpiHintColor = (key) => {
        if (key !== 'total' || totalDelta == null) return undefined;
        return totalDelta <= 0 ? 'var(--color-success)' : 'var(--color-danger)';
    };

    // Exposure = severity split, shown as a proportional segmented bar + chips.
    // Percentages are of the SCORED-risk sum (not the org total) — most risks may
    // be unscored, so a total-based denominator would leave the bar near-empty.
    const exposureData = [
        { key: 'critical', label: 'Critical', value: dash.severity.critical, color: 'var(--color-critical)', soft: 'var(--color-critical-soft)', desc: 'Immediate attention required' },
        { key: 'high', label: 'High', value: dash.severity.high, color: 'var(--color-high)', soft: 'var(--color-high-soft)', desc: 'Address as a priority' },
        { key: 'medium', label: 'Medium', value: dash.severity.medium, color: 'var(--color-medium)', soft: 'var(--color-medium-soft)', desc: 'Monitor and plan' },
        { key: 'low', label: 'Low', value: dash.severity.low, color: 'var(--color-low)', soft: 'var(--color-low-soft)', desc: 'Routine monitoring' },
    ];
    const sevSum = exposureData.reduce((a, s) => a + (s.value || 0), 0);
    const sevPct = (v) => (sevSum > 0 ? Math.round(((v || 0) / sevSum) * 100) : 0);

    // Category donut — dedupe the raw list (the backend can repeat a label, e.g.
    // two "Financial" rows), summing counts, then sort by volume.
    const categoryData = (() => {
        const map = new Map();
        for (const c of dash.byCategory || []) {
            const key = String(c.label || '').trim().toLowerCase();
            if (!key) continue;
            if (map.has(key)) map.get(key).value += c.value || 0;
            else map.set(key, { label: c.label, value: c.value || 0 });
        }
        return Array.from(map.values()).sort((a, b) => b.value - a.value);
    })();

    // Category legend as tiles — show only the top few categories to keep the
    // grid compact. Percentages use the ALL-categories total (catTotal) so each
    // card's % still reconciles with the donut centre (the true risk total),
    // even though only a subset of cards is shown. The donut itself still
    // renders every category.
    const CAT_CARD_LIMIT = 6;
    const catTotal = categoryData.reduce((a, s) => a + (s.value || 0), 0);
    const catSlices = categoryData.filter((s) => (s.value || 0) > 0).slice(0, CAT_CARD_LIMIT);

    return (
        <>
            {/* Title row — icon + "Risk Management" on the same line as the
                Setting / New Risk actions (moved here from the shell header). */}
            <div className='cmn_elevated_card p-3'>
                <div className="rp1_dash_header">
                    <div className="rp1_dash_greet">
                        <span className="rp1_dash_greet_icon"><ModuleIcon name="violation" size={26} /></span>
                        <div className="rp1_dash_card_titles">
                            <Heading variant={3} color="var(--color-text-primary)" style={{ margin: 0 }}>Risk Management</Heading>
                            <Body variant={2} color="var(--color-text-muted)" style={{ margin: 0 }}>
                                Org-wide risk analytics and portfolio overview.
                            </Body>
                        </div>
                    </div>
                    <div className="rp1_dash_header_actions">
                        {can && can('can_configure') && (
                            <CommonButton variant="secondary" text="Setting" onClick={() => goTo('reports')} />
                        )}
                        {can && can('can_create') && (
                            <CommonButton text="+ New Risk" onClick={onCreateRisk} />
                        )}
                    </div>
                </div>

                {/* Scope filters + export — second row */}
                <div className="rp1_dash_toolbar">
                    <CommonDropdown value={businessUnit} onChange={onBusinessUnit} options={dash.buOptions} ariaLabel="Business unit" minWidth={190} />
                    <CommonDropdown value={project} onChange={onProject} options={dash.projectOptions} ariaLabel="Project" minWidth={170} />
                    <CommonButton variant="secondary" text="Export Report" onClick={() => window.print()} />
                </div>

                {/* Severity KPI row — colour-tinted gradient wash, a saturated
                    icon on the left, and label / big value / hint stacked to its
                    right (mirrors the TPCRM analytics KPI cards). */}
                <div className="rp1_skpi_grid mt-4">
                    {KPIS.map((s) => (
                        <div
                            key={s.key}
                            className="rp1_skpi_card cmn_elevated_card"
                            style={{ background: `linear-gradient(135deg, ${s.soft} 0%, var(--color-surface) 62%)` }}
                        >
                            <span className="rp1_skpi_chip">
                                <ModuleIcon name={s.icon} size={28} color={s.color} />
                            </span>
                            <span className="rp1_skpi_text">
                                <Body as="span" variant={1} color="var(--color-text-secondary)">{s.label}</Body>
                                <Heading as="span" variant={3} className="rp1_skpi_value" color="var(--color-text-primary)">
                                    {severityValue(s.key)}
                                </Heading>
                                <Body as="span" variant={2} color={kpiHintColor(s.key)}>{kpiHint(s.key)}</Body>
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Row 1 — Left (60%): Exposure over Category. Right (40%): Top Risks. */}
            <div className="cmn_elevated_card p-3 rp1_dash_split">
                <div className="rp1_dash_split_left">
                    <div className="rp1_dash_card cmn_elevated_card">
                        <CardHead icon="score" title="Risk Exposure Overview" subtitle="Distribution across severity levels" />
                        {sevSum > 0 ? (
                            <div className="rp1_exposure">
                                <div className="rp1_exposure_head">
                                    <Heading variant={2} as="span" color="var(--color-text-primary)" style={{ margin: 0, lineHeight: 1 }}>{sevSum}</Heading>
                                    <Body as="span" variant={2} color="var(--color-text-muted)" style={{ margin: 0 }}>risks rated by severity</Body>
                                </div>
                                <div className="rp1_exposure_bar">
                                    {exposureData.map((s) => {
                                        const w = (s.value / sevSum) * 100;
                                        return w > 0 ? (
                                            <div
                                                key={s.key}
                                                className="rp1_exposure_seg"
                                                style={{ width: `${w}%`, background: s.color }}
                                                title={`${s.label}: ${s.value} (${sevPct(s.value)}%)`}
                                            />
                                        ) : null;
                                    })}
                                </div>
                                <div className="rp1_exposure_chips">
                                    {exposureData.map((s) => (
                                        <StatCard
                                            key={s.key}
                                            className="cmn_elevated_card"
                                            style={{ height: '100%' }}
                                            label={s.label}
                                            value={s.value}
                                            valueSize={24}
                                            hint={s.desc}
                                            icon={(
                                                <Body
                                                    as="span"
                                                    weight="bold"
                                                    style={{ color: s.color, background: s.soft, padding: '4px 12px', borderRadius: 'var(--radius-pill)', fontVariantNumeric: 'tabular-nums' }}
                                                >
                                                    {sevPct(s.value)}%
                                                </Body>
                                            )}
                                        />
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <NoDataPage hint="Severity breakdown appears once risks are scored." />
                        )}
                    </div>

                    <div className="rp1_dash_card cmn_elevated_card">
                        <CardHead icon="coverage" title="Risk by Category" subtitle="Where risk concentrates" />
                        {catSlices.length ? (
                            <div className="rp1_cat_split">
                                <RiskCategoryDonut data={categoryData} hideLegend />
                                <div className="rp1_cat_cards">
                                    {catSlices.map((s) => {
                                        const pct = catTotal > 0 ? Math.round(((s.value || 0) / catTotal) * 100) : 0;
                                        return (
                                            <AddOnCard
                                                key={s.label}
                                                className="cmn_elevated_card"
                                                // boxShadow:undefined lets the .cmn_elevated_card class shadow win
                                                // over AddOnCard's inline resting shadow.
                                                style={{ boxShadow: undefined }}
                                                label={<span style={{ textTransform: 'capitalize' }}>{s.label}</span>}
                                                action={(
                                                    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8, whiteSpace: 'nowrap' }}>
                                                        <Body as="span" weight="bold" color="var(--color-text-primary)" style={{ margin: 0, fontVariantNumeric: 'tabular-nums' }}>{s.value}</Body>
                                                        <Body as="span" variant={2} color="var(--color-text-muted)" style={{ margin: 0, minWidth: 34, textAlign: 'right' }}>{pct}%</Body>
                                                    </span>
                                                )}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            <NoDataPage hint="Categorise risks by type and they'll appear here." />
                        )}
                    </div>
                </div>

                <div className="rp1_dash_card cmn_elevated_card">
                    <CardHead
                        icon="violation"
                        title="Top Risks Requiring Attention"
                        subtitle="Highest-scored open risks"
                        action={<button type="button" className="rp1_link_btn" onClick={() => goTo('risk')}>View all →</button>}
                    />
                    {dash.topRisks.length ? (
                        <div className="rp1_toprisk_list">
                            {dash.topRisks.map((r) => (
                                <div key={r.id || r.risk_id} className="rp1_toprisk_item" onClick={() => onOpenRisk(r.id)}>
                                    <span className="rp1_toprisk_badge"><StatusBadge status={r.rating} label={r.rating} /></span>
                                    <div className="rp1_toprisk_text">
                                        <Body color="var(--color-text-primary)" weight="semibold" className="rp1_legend_ellipsis" style={{ margin: 0 }}>
                                            {r.risk || '—'}
                                        </Body>
                                        <Body variant={2} color="var(--color-text-muted)" style={{ margin: 0 }}>
                                            {r.risk_id || '—'} · Score {r.score ?? '—'} ·{' '}
                                            <Body as="span" variant={1} color="var(--color-primary)" weight="semibold">
                                                {r.owner || 'Unassigned'}
                                            </Body>
                                        </Body>
                                    </div>
                                    <Body as="span" className="rp1_toprisk_chev" style={{ margin: 0 }}>›</Body>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <NoDataPage hint="Top risks appear here once risks are scored." />
                    )}
                </div>
            </div>

            {/* Row 2 — Left (60%): Risk Trend over Treatment Progress.
                Right (40%): Risk Heatmap spanning the full height. */}
            <div className="cmn_elevated_card p-3 rp1_dash_split">
                <div className="rp1_dash_split_left">
                    <div className="rp1_dash_card cmn_elevated_card">
                        <CardHead icon="score" title="Risk Trend" subtitle="Open risks over the last 6 months" action={<TrendBadge series={dash.trend} />} />
                        <PortfolioTrendChart series={dash.trend} />
                    </div>

                    <div className="rp1_dash_card cmn_elevated_card">
                        <CardHead icon="check_circle" title="Treatment Progress" subtitle="Plan status across the register" />
                        <RiskCategoryDonut data={dash.treatment} showBars emptyHint="Treatment status appears once plans are created." />
                    </div>
                </div>

                <div className="rp1_dash_card cmn_elevated_card">
                    <CardHead icon="score" title="Risk Heatmap" subtitle="Likelihood × impact" />
                    {dash.heatmap?.matrix ? (
                        <RiskHeatmap matrix={dash.heatmap.matrix} />
                    ) : (
                        <NoDataPage hint="The heatmap populates once risks are assessed." />
                    )}
                </div>
            </div>

            {/* Row 3 — Risk distribution by Business Unit · by Project */}
            <div className="cmn_elevated_card p-3 rp1_dash_two">
                <div className="rp1_dash_card cmn_elevated_card">
                    <CardHead icon="building" title="Risk Distribution by Business Unit" subtitle="Severity split per business unit" />
                    {dash.buRows.length ? (
                        <>
                            <CommonTable headers={distributionHeaders('Business Unit')} rows={distributionRows(pageSlice(dash.buRows, buPage))} noPadding noScroll />
                            {pageCount(dash.buRows) > 1 && (
                                <CommonPagination handlePageClick={({ selected }) => setBuPage(selected)} forcePage={buPage} pageCount={pageCount(dash.buRows)} />
                            )}
                        </>
                    ) : (
                        <NoDataPage hint="Distribution appears once risks are assigned to business units." />
                    )}
                </div>

                <div className="rp1_dash_card cmn_elevated_card">
                    <CardHead icon="layers" title="Risk Distribution by Project" subtitle="Severity split per project" />
                    {dash.projectRows.length ? (
                        <>
                            <CommonTable headers={distributionHeaders('Project')} rows={distributionRows(pageSlice(dash.projectRows, projectPage))} noPadding noScroll />
                            {pageCount(dash.projectRows) > 1 && (
                                <CommonPagination handlePageClick={({ selected }) => setProjectPage(selected)} forcePage={projectPage} pageCount={pageCount(dash.projectRows)} />
                            )}
                        </>
                    ) : (
                        <NoDataPage hint="Distribution appears once risks are assigned to projects." />
                    )}
                </div>
            </div>
        </>
    );
}
